import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Field } from "../../components/site/ProfileFields";
import { FileUpload } from "../../components/site/FileUpload";
import { fileUrl } from "../../lib/api";
import { DateInput } from "../../components/site/DateInput";
import { taxKey, taxOptions } from "../../lib/taxValues";
import { useFetch } from "../../lib/useFetch";

const MultiPick = ({ options, value = [], onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map(([v, l]) => {
      const on = value.includes(v);
      return <button type="button" key={v} onClick={() => onChange(on ? value.filter((x) => x !== v) : [...value, v])} data-testid={`${testid}-${l}`}
        className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${on ? "border-[#00A8B5] bg-[#00A8B5] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{l}</button>;
    })}
  </div>
);

const useOptions = (src, value) => {
  const { data } = useFetch(src ? (src.tax ? "/admin/tax-values" : src.res ? `/admin/${src.res}` : null) : null);
  if (!src) return [];
  if (src.tax) return taxOptions(data, value);
  if (src.static) return src.static.map((x) => (Array.isArray(x) ? x : [x, x]));
  return (data || []).slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99)).map((d) => [d[src.value || "id"], src.format ? src.format(d) : d[src.label || "name"]]);
};

export const FormField = ({ field, value, onChange, prefix = "" }) => {
  const opts = useOptions(field.options, value);
  const tid = `field-${prefix}${field.key.replace(/[._]/g, "-")}`;
  let el;
  if (field.type === "textarea") el = <Textarea rows={field.rows || 3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  else if (field.type === "number") el = <Input type="number" step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={tid} />;
  else if (field.type === "bool") el = <Switch checked={!!value} onCheckedChange={onChange} data-testid={tid} />;
  else if (field.type === "select") el = (
    <select value={field.options?.tax ? taxKey(value) : value || ""} onChange={(e) => onChange(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid={tid}>
      {(!field.required || (field.options?.tax && taxKey(value) === "")) && <option value="">{field.emptyLabel || "—"}</option>}{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>);
  else if (field.type === "multi") el = <MultiPick options={opts} value={value || []} onChange={onChange} testid={tid} />;
  else if (field.type === "image") el = (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        {value && <img src={fileUrl(value)} alt="" className="h-14 w-20 rounded-lg object-cover" />}
        <FileUpload accept="image/*" isPublic label="Upload" onUploaded={(x) => onChange(`${process.env.REACT_APP_BACKEND_URL}/api/files/${x.path}`)} testid={`${tid}-upload`} />
      </div>
      <Input placeholder="or paste image URL" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />
    </div>);
  else if (field.type === "date") el = <DateInput value={value || ""} onChange={onChange} data-testid={tid} />;
  else el = <Input type={field.type || "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  return <div className={field.full ? "md:col-span-2" : ""}><Field label={field.label}>{el}</Field></div>;
};

export const FormGrid = ({ fields, f, setF, prefix = "" }) => (
  <div className="grid gap-4 md:grid-cols-2">
    {fields.map((fd) => <FormField key={fd.key} prefix={prefix} field={fd} value={f[fd.key]} onChange={(v) => setF((p) => ({ ...p, [fd.key]: v }))} />)}
  </div>
);
